import type { ChipTone } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import type {
  ConcernCriterionKind,
  WorkItemActorRole,
  WorkItemDecisionCode,
  WorkItemEventKind,
  IWorkItemRow,
  WorkItemOrigin,
  WorkItemState,
  WorkItemTrigger
} from './api/work-item-contracts';
import { slotWords } from './roster-words';

/**
 * The work-item queue's words, and the two things about it this side mirrors (SR-222, F9's R2,
 * R3, R14). Every screen of the queue — the compliance queues, My tasks, the item's pane, the
 * hub's Work items — draws a trigger, a state or an event through this one module, so a word
 * changes in one place.
 *
 * **A value with no word here is drawn as spelled**, never dropped and never mapped to a near
 * neighbour: the label functions answer the stored spelling for a code they do not know, the
 * activity feed's rule. survey-api adds vocabulary on its own schedule, and a trigger the screen
 * cannot name is still an item somebody has to decide.
 *
 * Two tables here are **mirrors** of survey-api, and move with it — the `COLUMN_DEFAULTS`
 * precedent:
 *
 * - `WORK_ITEM_QUEUES` mirrors `WorkItemQueues.All` (`Domain/WorkItems/WorkItemQueues.cs`): the
 *   queues an item can land in. The summary route lists only the queues that hold an item, and a
 *   tab that appears with the first item and vanishes with the last would move every tab beside
 *   it, so the tabs come from this list and the summary only counts them.
 * - `WORK_ITEM_MOVES` mirrors the design's §6 as `WorkItem.Decide` and `.Reopen` enforce it
 *   (`Domain/WorkItems/WorkItem.cs`): the wire carries no `allowedDecisions`, and a control the
 *   state refuses is better not drawn than drawn and refused. survey-api still judges every move
 *   — a raced one comes back 409 `invalid-transition`, read by `transitionRefusal` below.
 *
 * Neither is a rule run here: no due date is computed, no item is placed in a queue, and what
 * the mirror lacks (a queue survey-api adds) is drawn as sent.
 */

/** `WorkItemQueues.All`, in the tabs' order (F9's R2) rather than the C# array's, which orders
 *  for its placement rule. */
export const WORK_ITEM_QUEUES = ['FA', 'TEAMS', 'FSP', 'RIA_FIRM', 'CANADA', 'CANADA_FR'] as const;

/** One queue code — `WORK_ITEM_QUEUES`' members. A row's `surveyCode` stays a `string`. */
export type WorkItemQueue = (typeof WORK_ITEM_QUEUES)[number];

/** Whether the mirror knows a queue code — one it lacks is still drawn, as spelled. */
export function isWorkItemQueue(code: string): boolean {
  return (WORK_ITEM_QUEUES as readonly string[]).includes(code);
}

/** What a state offers (design §6): every unclosed state takes every decision — a parked item
 *  takes a decision, and `pending_firm` again sets a new date — and only a reviewed one reopens. */
export interface IWorkItemMoves {
  decisions: readonly WorkItemDecisionCode[];
  reopen: boolean;
}

const ALL_DECISIONS: readonly WorkItemDecisionCode[] = ['keep', 'remove', 'escalate', 'pending_firm'];

export const WORK_ITEM_MOVES: Record<'open' | 'pending_firm' | 'escalated' | 'reviewed', IWorkItemMoves> =
  {
    open: { decisions: ALL_DECISIONS, reopen: false },
    pending_firm: { decisions: ALL_DECISIONS, reopen: false },
    escalated: { decisions: ALL_DECISIONS, reopen: false },
    reviewed: { decisions: [], reopen: true }
  };

/** What a state offers, by the mirror — and nothing for a state the mirror does not know: a
 *  control offered on a state nobody here has read about would be a guess. */
export function movesOf(state: WorkItemState): IWorkItemMoves {
  return (WORK_ITEM_MOVES as Record<string, IWorkItemMoves>)[state] ?? { decisions: [], reopen: false };
}

/** The decisions every one of `states` offers — the bulk bar's set (R7). */
export function sharedDecisions(states: readonly WorkItemState[]): WorkItemDecisionCode[] {
  return ALL_DECISIONS.filter((decision) =>
    states.every((state) => movesOf(state).decisions.includes(decision))
  );
}

/** survey-api's `WorkItemStates.Unclosed` — what My tasks, Team Home's summary and the hub's
 *  open items narrow by. */
export const UNCLOSED_STATES: readonly WorkItemState[] = ['open', 'pending_firm', 'escalated'];

function labelOf(table: Record<string, string>, code: string): string {
  return table[code] ?? code;
}

const TRIGGER_LABEL: Record<string, string> = {
  new_disclosure: 'New disclosure',
  changed_disclosure: 'Changed disclosure',
  threshold: 'Threshold crossed',
  brokercheck_error: 'BrokerCheck error',
  crd_or_firm_changed: 'CRD or firm changed',
  manual: 'Raised by staff'
};

/** The triggers this build has words for, in the filter's order — the boxes behind the queue's
 *  "Raised by" chip. Not a mirror: a trigger survey-api adds is still drawn, as spelled. */
export const WORK_ITEM_TRIGGERS: readonly WorkItemTrigger[] = Object.keys(TRIGGER_LABEL);

/** What raised an item, in words — the stored spelling for a trigger this table lacks. */
export function triggerLabel(trigger: WorkItemTrigger): string {
  return labelOf(TRIGGER_LABEL, trigger);
}

const STATE_LABEL: Record<string, { label: string; tone: ChipTone }> = {
  open: { label: 'Outstanding', tone: 'info' },
  pending_firm: { label: 'Pending firm', tone: 'attention' },
  escalated: { label: 'Escalated', tone: 'attention' },
  reviewed: { label: 'Reviewed', tone: 'neutral' }
};

/** A state as its chip draws it. An unknown state is spelled and `neutral`: a tone is a claim
 *  about what the state means, which nobody here can make for one it has not read about. */
export function stateChip(state: WorkItemState): { label: string; tone: ChipTone } {
  return STATE_LABEL[state] ?? { label: state, tone: 'neutral' };
}

const DECISION_LABEL: Record<WorkItemDecisionCode, string> = {
  keep: 'Keep',
  remove: 'Remove',
  escalate: 'Escalate',
  pending_firm: 'Pending firm'
};

/**
 * An item named where a list must tell rows apart without its table — a selection box, a bulk
 * refusal: its subject and whose it is (and the member it is about, where that is someone else).
 * The subject alone is not enough: a rule item's subject is its criterion's name, which every item
 * that criterion raised shares (the owner's ruling at F9's gate 2).
 */
export function workItemName(row: IWorkItemRow): string {
  const about =
    row.memberName && row.memberName !== row.participant.displayName ? ` (${row.memberName})` : '';
  return `${row.subject} — ${row.participant.displayName}${about}`;
}

/**
 * The team member an item is about, as the row names it (SR-396 W3): the name, then the roster
 * slot listing the item's CRD ("Advisor 2", "Lead advisor") where survey-api sends both the slot
 * and its role, then the CRD — each part only where it is sent, `[]` when none is. A person's own
 * item carries neither a name nor a slot, and its CRD is then the person's.
 */
export function memberParts(row: IWorkItemRow): string[] {
  const parts: string[] = [];
  if (row.memberName) parts.push(row.memberName);
  if (row.memberSlot != null && row.memberRole != null) {
    parts.push(slotWords({ role: row.memberRole, slot: row.memberSlot }));
  }
  if (row.crd) parts.push(`CRD ${row.crd}`);
  return parts;
}

/** Whether an item is about a team's member rather than its participant — a name or a slot sent
 *  (W3); the queue's line under the participant is drawn for these alone. */
export function isAboutMember(row: IWorkItemRow): boolean {
  return Boolean(row.memberName) || row.memberSlot != null;
}

const LOOKUP_LABEL: Record<string, string> = {
  brokercheck: 'BrokerCheck lookup',
  iapd: 'IAPD lookup'
};

/** The pane's label for the lookup an item's fact came from (the owner's Q3): the registry that
 *  answered, by `lookupSource` — "BrokerCheck lookup" where it is absent or null (an API before
 *  SR-396, when BrokerCheck was the only one), a source this table lacks by its code. */
export function lookupLabel(source: Nullable<string> | undefined): string {
  if (source == null) return LOOKUP_LABEL.brokercheck;
  return LOOKUP_LABEL[source] ?? `${source} lookup`;
}

/** A decision in words; the stored spelling for one this table lacks. */
export function decisionLabel(decision: string): string {
  return labelOf(DECISION_LABEL, decision);
}

const EVENT_LABEL: Record<string, string> = {
  raised: 'Raised',
  decided: 'Decided',
  assigned: 'Assigned',
  follow_up_set: 'Follow-up set',
  reopened: 'Reopened',
  follow_up_due: 'Follow-up due — back to Outstanding',
  closed_in_salesforce: 'Closed in Salesforce'
};

/** One history line's kind in words; the stored spelling for one this table lacks. */
export function eventLabel(kind: WorkItemEventKind): string {
  return labelOf(EVENT_LABEL, kind);
}

/** Whether `eventLabel` knows the kind — a screen draws the stored spelling beside an unknown
 *  one's, so the reader is not left guessing which word is ours. */
export function isKnownEvent(kind: WorkItemEventKind): boolean {
  return kind in EVENT_LABEL;
}

const ORIGIN_LABEL: Record<string, string> = {
  rule: 'A concern criterion',
  manual: 'Staff',
  salesforce: 'Salesforce'
};

/** Who raised an item, in words; the stored spelling for one this table lacks. */
export function originLabel(origin: WorkItemOrigin): string {
  return labelOf(ORIGIN_LABEL, origin);
}

const ACTOR_ROLE_LABEL: Record<string, string> = {
  staff: 'Staff',
  admin: 'Admin',
  system: 'System',
  migration: 'Salesforce migration'
};

/** An actor role in words — what a history line names where no account acted. */
export function actorRoleLabel(role: WorkItemActorRole): string {
  return labelOf(ACTOR_ROLE_LABEL, role);
}

const KIND_LABEL: Record<string, string> = {
  disclosure: 'One disclosure',
  aggregate: 'A person’s disclosures'
};

/** A criterion's kind in words; the stored spelling for one this table lacks. */
export function criterionKindLabel(kind: ConcernCriterionKind): string {
  return labelOf(KIND_LABEL, kind);
}

/** What a work item's 409 `invalid-transition` says — survey-api's sentence and every refused
 *  id. */
export interface ITransitionRefusal {
  reason: Nullable<string>;
  itemIds: string[];
}

/**
 * The refusal of a move the item's state did not take, or `null` for any other error. Read off
 * the problem's `type` and its two extensions, never off `title`: the definitions surface answers
 * the same slug for retiring a draft, without either member, which reads here as no reason and
 * no ids.
 */
export function transitionRefusal(error: unknown): Nullable<ITransitionRefusal> {
  if (!(error instanceof ApiError) || !error.isProblem(ProblemType.invalidTransition)) return null;
  const { reason, itemIds } = error.problem ?? {};
  return {
    reason: typeof reason === 'string' ? reason : null,
    itemIds: Array.isArray(itemIds) ? itemIds.filter((id) => typeof id === 'string') : []
  };
}
